import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  User,
  ShieldAlert,
  Building,
  Swords,
  Trophy,
  ClipboardCheck,
  CalendarDays,
  Scale,
  CircleHelp,
  LogOut,
  Menu,
  MapPinned
} from "lucide-react";
import { Auth } from "aws-amplify";
import { hasAnyRole } from "../lib/roles";
import { getRoleScopeHomePath, getScopedNavPath, type ActiveRoleScope } from "../lib/roleContext";

interface SidebarProps {
  userRoles: string[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
  roleScopes?: ActiveRoleScope[];
  activeRoleScope?: ActiveRoleScope | null;
  onRoleScopeChange?: (scopeId: string) => void;
}

type NavItem = {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles: string[];
};

type NavSection = {
  label: string;
  items: NavItem[];
};

const navSections: NavSection[] = [
  {
    label: "Home",
    items: [
      { label: "Dashboard", path: "/admin/dashboard", icon: <LayoutDashboard size={18} />, roles: [] },
      { label: "Pitch Logs", path: "/admin/pitch-logs", icon: <LayoutDashboard size={18} />, roles: [] },
      { label: "Violations", path: "/admin/violations", icon: <ShieldAlert size={18} />, roles: ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish", "Coach", "Parent", "Parents"] }
    ]
  },
  {
    label: "Roster",
    items: [
      { label: "Teams", path: "/admin/teams", icon: <Users size={18} />, roles: ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish", "Coach"] },
      { label: "Players", path: "/admin/players", icon: <User size={18} />, roles: ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish", "Coach", "Parent", "Parents"] }
    ]
  },
  {
    label: "Program Ops",
    items: [
      { label: "Organizations", path: "/admin/organizations", icon: <Building size={18} />, roles: ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish"] },
      { label: "Programs", path: "/admin/programs", icon: <Building size={18} />, roles: ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish"] },
      { label: "Leagues", path: "/admin/leagues", icon: <Swords size={18} />, roles: ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish"] },
      { label: "Seasons", path: "/admin/seasons", icon: <CalendarDays size={18} />, roles: ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish"] },
      { label: "Rules", path: "/admin/rules", icon: <Scale size={18} />, roles: ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish"] },
      { label: "Schedule", path: "/admin/schedule", icon: <MapPinned size={18} />, roles: ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish", "Coach"] }
    ]
  },
  {
    label: "Player Pipeline",
    items: [
      { label: "Evaluations", path: "/admin/evaluations", icon: <ClipboardCheck size={18} />, roles: ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish", "Coach", "Evaluator"] },
      { label: "Drafts", path: "/admin/drafts", icon: <Trophy size={18} />, roles: ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish"] }
    ]
  },
  {
    label: "Admin",
    items: [
      { label: "Users", path: "/admin/users", icon: <Users size={18} />, roles: ["SuperAdmin", "OrganizationAdmin", "LeagueAdmin", "Commish"] },
      { label: "Help", path: "/admin/help", icon: <CircleHelp size={18} />, roles: [] },
      { label: "Logout", path: "/logout", icon: <LogOut size={18} />, roles: [] }
    ]
  }
];

const canSeeItem = (item: NavItem, userRoles: string[]) => hasAnyRole(userRoles, item.roles);

const isActivePath = (currentPath: string, itemPath: string) => {
  if (itemPath === "/admin/dashboard") {
    return currentPath === itemPath;
  }
  if (itemPath === "/admin/teams") {
    return currentPath === itemPath || currentPath.startsWith("/admin/teams/");
  }
  if (itemPath === "/admin/players") {
    return currentPath === itemPath || currentPath.startsWith("/admin/players/");
  }
  return currentPath === itemPath;
};

export default function Sidebar({
  userRoles,
  collapsed,
  onToggleCollapsed,
  roleScopes = [],
  activeRoleScope = null,
  onRoleScopeChange
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        const displayName = user?.attributes?.email ?? user?.username ?? "";
        setUsername(displayName);
      })
      .catch(() => setUsername(""));
  }, []);

  const handleRoleScopeChange = (scopeId: string) => {
    onRoleScopeChange?.(scopeId);
    const nextScope = roleScopes.find((scope) => scope.id === scopeId);
    navigate(getRoleScopeHomePath(nextScope), { replace: false });
  };

  return (
    <aside className={`sidebar ${collapsed ? "sidebar--collapsed" : ""}`}>
      <button
        onClick={onToggleCollapsed}
        className="sidebar__toggle mb-4 focus:outline-none"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <Menu size={18} />
      </button>
      <div className={`sidebar__brand ${collapsed ? "sidebar__brand--hidden" : ""}`}>
        <div className="sidebar__logoShell">
          <img
            src="/logo512.png"
            alt="LEGENDS FIELD logo"
            className="sidebar__logo"
            onError={(event) => {
              const target = event.currentTarget;
              target.style.display = "none";
            }}
          />
        </div>
        <div className="sidebar__welcome">LEGENDS FIELD</div>
        <div className="text-sm text-gray-600 mb-4">{username}</div>
        {roleScopes.length > 0 && (
          <label className="sidebar__rolePicker" aria-label="Current role scope">
            <span>Working as</span>
            <select value={activeRoleScope?.id ?? ""} onChange={(event) => handleRoleScopeChange(event.target.value)}>
              {roleScopes.map((scope) => (
                <option key={scope.id} value={scope.id}>
                  {scope.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="sidebar__subtitle">Protecting Young Arms</div>
      </div>
      <nav className="space-y-2 sidebar__nav">
        {navSections.map((section) => {
          const visibleItems = section.items.filter((item) => canSeeItem(item, userRoles));
          if (visibleItems.length === 0) {
            return null;
          }
          return (
            <div key={section.label} className="sidebar__section">
              {!collapsed && <div className="sidebar__section-label">{section.label}</div>}
              {visibleItems.map((item) => (
                <Link
                  key={`${section.label}-${item.label}`}
                  to={getScopedNavPath(item.path, activeRoleScope)}
                  title={collapsed ? item.label : undefined}
                  className={`sidebar__link ${collapsed ? "sidebar__link--collapsed" : ""} ${
                    isActivePath(location.pathname, item.path) ? "sidebar__link--active font-medium" : "text-gray-700"
                  }`}
                >
                  <span className="sidebar__icon">{item.icon}</span>
                  {!collapsed && <span className="sidebar__label">{item.label}</span>}
                </Link>
              ))}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
